import type { MiraRouteId } from '@mira/clients-shared';
import { ButtonLink, IconGlyph } from '../../ui/primitives/index.js';
import { DetailTopbar } from '../../ui/patterns/index.js';
import { hrefForRoute } from '../../app/router.js';

export function LegalScreen(props: { kind: 'privacy' | 'terms'; source?: MiraRouteId }) {
  const title = props.kind === 'privacy' ? 'Privacy Policy' : 'Terms of Service';
  const fromSettings = props.source === 'settings' || props.source === 'settings-privacy';
  const sections = props.kind === 'privacy' ? privacySections : termsSections;
  const backHref = props.source === 'settings-privacy'
    ? hrefForRoute('settings-privacy')
    : fromSettings ? hrefForRoute('settings') : hrefForRoute(props.source || 'login');
  return (
    <article className="mira-business-screen mira-legal-screen">
      <DetailTopbar backHref={backHref} backLabel="Back" label="Mira" />
      <header className="mira-legal-header">
        <h1>{title}</h1>
        {fromSettings ? <p><IconGlyph value="▱" /> Opened from Settings</p> : null}
        <small>Last updated: August 27, 2026</small>
      </header>
      <section className="mira-legal-page mira-legal-copy">
        {sections.map((section, index) => (
          <section className="mira-legal-section" key={section.title}>
            <h2><IconGlyph value={section.icon} /> {index + 1}. {section.title}</h2>
            <p>{section.body}</p>
          </section>
        ))}
        <p className="mira-legal-contact"><IconGlyph value="○" /> Questions? Reach us through the support channel provided by the operator.</p>
      </section>
      {fromSettings ? <ButtonLink href={backHref} variant="primary">Back to Settings</ButtonLink> : null}
    </article>
  );
}

const privacySections = [
  {
    icon: '♧',
    title: 'What we process',
    body: 'To provide the service, Mira processes your account details, the birth facts you enter, conversation content, important relationships, and the topics, suggested memories, and insights formed from conversations. Unknown information is never silently filled in as fact.',
  },
  {
    icon: '▱',
    title: 'How your content is used',
    body: 'Your content keeps conversations continuous, grounds the context of you and your important relationships, and produces reviewable objects. Mira never exposes internal reasoning terms, file paths, or service credentials. This version has no standalone notes library, and frontend samples are never treated as your real content.',
  },
  {
    icon: '☷',
    title: 'How you stay in control',
    body: 'Edit your profile and birth details, confirm or delete suggested memories, and delete topics, insights, and relationships. Some deletions only affect the matching object — deleting a relationship never auto-deletes past conversations. The impact is always explained before you confirm.',
  },
  {
    icon: '⌫',
    title: 'Security and current limits',
    body: 'Sign-in sessions and per-account ownership checks isolate data across accounts; secrets stay out of the frontend and ordinary content. This web version is still maturing: full account export and account deletion are not open in the product yet, and we will not claim support before they exist.',
  },
];

const termsSections = [
  {
    icon: '§',
    title: 'Using Mira',
    body: 'Mira is a reflective service that helps you untangle life, relationships, and present questions. It offers understanding and a gentle next step; it does not replace medical, psychological, legal, financial, or other professional services. You remain responsible for your real-world decisions.',
  },
  {
    icon: '♧',
    title: 'Your account and content',
    body: "Keep your credentials safe and only use Mira with content you have the right to provide. Do not submit other people's secrets, unlawful content, or material meant to harm. You can change your profile, birth details, and password in Settings.",
  },
  {
    icon: '▱',
    title: 'What this version covers',
    body: 'This version covers the core web experience: identity, birth details, continuous conversation, topics, memory, insights, and relationships. Payments, a standalone notes library, other languages, and other clients are out of scope; no fees are charged and no availability is claimed until they truly ship.',
  },
  {
    icon: '○',
    title: 'Changes and discontinuation',
    body: 'We update this page when capabilities or terms change materially. When the service cannot complete a request, it shows failure or unfinished state explicitly — never a fake success.',
  },
];
